import { useCallback, useEffect, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import {
  EnrichableOrder,
  applyConsignmentStatusPatch,
  mapOrdersListApiRow,
} from '../utils/orderDetailEnrichment';
import {
  EnrichRefreshResult,
  usePalletwaysOrderDetailEnrich,
} from './usePalletwaysOrderDetailEnrich';

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  );
}

/**
 * On-demand order-detail completeness indicator for Profit Compute.
 * Does not auto-fetch on date change (avoids overlapping multi-MB downloads / OOM).
 * Trigger via `runCheck()` — typically from "Check sources". Indicator only; does not block Compute.
 */
export function usePeriodOrderDetailCheckpoint(startDate: string, endDate: string) {
  const [orders, setOrders] = useState<EnrichableOrder[]>([]);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  /** True after a successful check for the current period (cleared when dates change). */
  const [hasChecked, setHasChecked] = useState(false);
  const fetchGenRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  /** When true, next successful fetch is a silent post-enrich refresh (keep hasChecked). */
  const silentRefreshRef = useRef(false);

  const clearCheck = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    fetchGenRef.current += 1;
    silentRefreshRef.current = false;
    setOrders([]);
    setCheckError(null);
    setChecking(false);
    setHasChecked(false);
  }, []);

  useEffect(() => {
    clearCheck();
  }, [startDate, endDate, clearCheck]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const runCheck = useCallback(async () => {
    if (!startDate || !endDate) return;

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    const gen = ++fetchGenRef.current;
    const silent = silentRefreshRef.current;
    silentRefreshRef.current = false;

    if (!silent) {
      setChecking(true);
      setCheckError(null);
    }

    try {
      const from = `${startDate}T00:00:00.000Z`;
      const to = `${endDate}T23:59:59.999Z`;
      const res = await authenticatedFetch(
        API_ENDPOINTS.fleetIntelligence.orders.list(
          { from, to },
          { skipSameDayConfirm: true },
        ),
        { signal: ac.signal },
      );
      if (gen !== fetchGenRef.current || ac.signal.aborted) return;
      if (!res.ok) {
        throw new Error(`Orders list failed (${res.status})`);
      }
      const data = await res.json();
      if (gen !== fetchGenRef.current || ac.signal.aborted) return;
      const rawList = Array.isArray(data?.orders) ? data.orders : Array.isArray(data) ? data : [];
      const mapped = (rawList as Record<string, unknown>[]).map(mapOrdersListApiRow);
      setOrders(mapped);
      setHasChecked(true);
      setCheckError(null);
    } catch (err) {
      if (gen !== fetchGenRef.current || isAbortError(err) || ac.signal.aborted) {
        return;
      }
      setCheckError(err instanceof Error ? err.message : 'Failed to check order details');
      if (!silent) {
        setOrders([]);
        setHasChecked(false);
      }
    } finally {
      if (gen === fetchGenRef.current && !silent) {
        setChecking(false);
      }
    }
  }, [startDate, endDate]);

  const refreshOrder = useCallback(
    async (order: EnrichableOrder, options?: { silent?: boolean }): Promise<EnrichRefreshResult> => {
      const trackingId = String(order.trackingId || '').trim();
      if (!trackingId) return { ok: false };
      try {
        const response = await authenticatedFetch(
          API_ENDPOINTS.fleetIntelligence.palletwaysConsignmentStatus(),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              trackingId,
              consignmentNo: order.name || undefined,
              currentStatus: order.status || undefined,
            }),
          },
        );
        const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
        if (response.status === 429) {
          return {
            ok: false,
            rateLimited: true,
            retryAfterMs: Number(body?.retryAfterMs) || 5000,
          };
        }
        if (!response.ok) {
          if (!options?.silent) {
            console.error('[PeriodOrderCheckpoint] enrich failed:', body?.error || response.status);
          }
          return { ok: false };
        }
        const nextStatus = String(body?.palletwaysStatus || '').trim();
        if (!nextStatus && !body?.detailRepaired) {
          return { ok: false };
        }
        setOrders((prev) =>
          prev.map((row) => applyConsignmentStatusPatch(row, order, trackingId, body)),
        );
        return { ok: true };
      } catch (err) {
        if (!options?.silent) {
          console.error('[PeriodOrderCheckpoint] enrich failed:', err);
        }
        return { ok: false };
      }
    },
    [],
  );

  // Deliberate-only enrich: auto-start on mount flooded
  // truth-consignment-status-repair-requested (no durable cross-session dedup).
  // Keep the loop enabled so Fill missing details works, but manualOnly so it
  // never runs until the operator clicks.
  const enrich = usePalletwaysOrderDetailEnrich({
    orders,
    enabled: true,
    manualOnly: true,
    refreshOrder,
  });

  // After a batch finishes, silently re-fetch so the indicator matches server truth.
  const prevPendingRef = useRef(enrich.bgEnrichPending);
  const prevBlankRef = useRef(enrich.blankDetailsCount);
  useEffect(() => {
    if (!hasChecked) return;
    const prevPending = prevPendingRef.current;
    const prevBlank = prevBlankRef.current;
    prevPendingRef.current = enrich.bgEnrichPending;
    prevBlankRef.current = enrich.blankDetailsCount;

    const batchFinished =
      prevPending > 0 && enrich.bgEnrichPending === 0 && !enrich.bgEnrichPaused;
    const blanksCleared = prevBlank > 0 && enrich.blankDetailsCount === 0;
    if (batchFinished || blanksCleared) {
      silentRefreshRef.current = true;
      void runCheck();
    }
  }, [
    hasChecked,
    enrich.bgEnrichPending,
    enrich.bgEnrichPaused,
    enrich.blankDetailsCount,
    runCheck,
  ]);

  return {
    checking,
    checkError,
    hasChecked,
    runCheck,
    clearCheck,
    blankDetailsCount: enrich.blankDetailsCount,
    missingDetailsCount: enrich.missingDetailsCount,
    bgEnrichPending: enrich.bgEnrichPending,
    bgEnrichPaused: enrich.bgEnrichPaused,
    userPaused: enrich.userPaused,
    pipelineGateDepths: enrich.pipelineGateDepths,
    enrichUsed: enrich.enrichUsed,
    enrichBudget: enrich.enrichBudget,
    startOrContinueEnrichBatch: enrich.startOrContinueEnrichBatch,
    pauseEnrichBatch: enrich.pauseEnrichBatch,
    PIPELINE_SAFE_DEPTH: enrich.PIPELINE_SAFE_DEPTH,
  };
}
